import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowRight, MapPin, Megaphone, MessageCircleQuestion, Lightbulb, Home as HomeIcon, Wrench, ExternalLink, X } from 'lucide-react'
import { SITE } from '../constants/site'
import { listAnnonces } from '../api/annonces'
import { TexteRiche } from '../lib/TexteRiche'
import { BrandShowcase } from '../components/BrandShowcase'
import { Hero } from '../components/Hero'
import { useEscapeKey } from '../lib/useEscapeKey'
import { useSeo } from '../lib/useSeo'
import { getSiteLinks } from '../api/site'
import { ACCOMPAGNEMENT } from '../constants/products'

const ETAPE_ICONS = [Lightbulb, HomeIcon, Wrench]
const MIIMO_URL = 'https://www.fr.honda.be/lawn-and-garden/products/miimo'
const AUTOMOWER_URL = 'https://www.husqvarna.com/be-fr/robots-tondeuses/'

export function Home() {
  const { data: annonces = [] } = useQuery({ queryKey: ['annonces'], queryFn: listAnnonces })
  const [redirect, setRedirect] = useState<{ url: string; marque: string } | null>(null)
  const [showMap, setShowMap] = useState(false)
  useSeo(
    'Maison Gilles · Robots tondeuses & matériel de jardin — Jambes (Namur)',
    'Maison Gilles, votre spécialiste en matériel de jardin à Jambes (Namur) depuis 1925. Vente, réparation et entretien de robots tondeuses Honda & Husqvarna, tondeuses, tronçonneuses, taille-haies.',
  )
  const { data: links } = useQuery({ queryKey: ['site-links'], queryFn: getSiteLinks })
  const miimoUrl = links?.lien_miimo ?? MIIMO_URL
  const automowerUrl = links?.lien_automower ?? AUTOMOWER_URL
  useEscapeKey(() => setRedirect(null))

  function confirmer() {
    if (redirect) window.open(redirect.url, '_blank', 'noopener,noreferrer')
    setRedirect(null)
  }

  return (
    <>
      {/* ───────────── ANNONCES ───────────── */}
      {annonces.length > 0 && (
        <div className="bg-orange text-white">
          <div className="mx-auto flex max-w-6xl flex-col gap-1.5 px-5 py-2.5 text-sm lg:px-8">
            {annonces.map((a) => (
              <div key={a.id} className={`flex items-start gap-3 ${a.centre ? 'justify-center' : ''}`}>
                <Megaphone className="mt-0.5 h-4 w-4 shrink-0" />
                <div className={`space-y-2 ${a.centre ? 'text-center' : ''}`}>
                  {a.titre && (
                    <p className="font-semibold leading-snug">{a.titre}</p>
                  )}
                  <TexteRiche texte={a.texte} className="leading-snug" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ───────────── HERO CINÉMATIQUE ───────────── */}
      <Hero />

      {/* ───────────── NOS MARQUES (2 bandeaux à défilement auto) ───────────── */}
      <BrandShowcase />

      {/* ───────────── ROBOTS TONDEUSES ───────────── */}
      <section id="robots" className="relative overflow-hidden bg-navy py-20 text-white lg:py-24">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          {/* En-tête */}
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-orange">
              Robots tondeuses
            </p>
            <h2 className="mt-3 font-display text-3xl font-bold sm:text-4xl">
              Une tonte simple et automatique
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-white/85">
              Découvrez notre gamme de robots tondeuses Husqvarna &amp; Honda. L'outil idéal pour
              entretenir votre jardin sans vous en soucier.
            </p>
          </div>

          {/* Les deux marques */}
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            <article className="flex flex-col overflow-hidden rounded-2xl bg-white/5 ring-1 ring-white/10 transition-colors hover:ring-white/25">
              <div className="flex h-52 items-center justify-center bg-white/95 p-6">
                <img src="/img/robot-miimo-honda.png" alt="Robot Honda Miimo" loading="lazy" decoding="async" className="max-h-40 w-auto object-contain" />
              </div>
              <div className="flex flex-1 flex-col p-6">
                <h3 className="font-display text-xl font-bold text-white">Honda Miimo</h3>
                <p className="mb-6 mt-3 text-sm leading-relaxed text-white/75">
                  Fiabilité et ingénierie japonaise : les Miimo assurent une tonte précise et régulière,
                  en toute simplicité. Robustes et silencieux, ils s'adaptent à la plupart des jardins et
                  bénéficient d'une garantie jusqu'à 5 ans.
                </p>
                <a
                  href={miimoUrl}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => { e.preventDefault(); setRedirect({ url: miimoUrl, marque: 'Honda' }) }}
                  className="mt-auto inline-flex w-fit items-center gap-2 rounded-full bg-orange px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-orange-dk"
                >
                  Découvrir les Miimo <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            </article>
            <article className="flex flex-col overflow-hidden rounded-2xl bg-white/5 ring-1 ring-white/10 transition-colors hover:ring-white/25">
              <div className="flex h-52 items-center justify-center bg-white/95 p-6">
                <img src="/img/robot-husqvarna-430v.png" alt="Robot Husqvarna Automower" loading="lazy" decoding="async" className="max-h-40 w-auto object-contain" />
              </div>
              <div className="flex flex-1 flex-col p-6">
                <h3 className="font-display text-xl font-bold text-white">Husqvarna Automower®</h3>
                <p className="mb-6 mt-3 text-sm leading-relaxed text-white/75">
                  Leader mondial du robot tondeuse, Husqvarna mise sur l'innovation suédoise : installation
                  avec ou sans câble, navigation intelligente et pilotage par smartphone. Certains modèles
                  gèrent des pentes jusqu'à 70 % et bénéficient d'une garantie jusqu'à 4 ans.
                </p>
                <a
                  href={automowerUrl}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => { e.preventDefault(); setRedirect({ url: automowerUrl, marque: 'Husqvarna' }) }}
                  className="mt-auto inline-flex w-fit items-center gap-2 rounded-full bg-orange px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-orange-dk"
                >
                  Découvrir les Automower <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* ───────────── ROBOTS : QUESTIONNAIRE ───────────── */}
      <section className="bg-orange py-12 text-white">
        <div className="mx-auto flex max-w-5xl flex-col items-center gap-5 px-5 text-center lg:px-8">
          <MessageCircleQuestion className="h-10 w-10" />
          <h2 className="font-display text-2xl font-bold sm:text-3xl">
            Trouvez le robot qui vous correspond !
          </h2>
          <Link to="/#contact" className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3.5 text-sm font-semibold text-orange transition-colors hover:bg-white/90">
            Contactez-nous <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* ───────────── ROBOTS : ACCOMPAGNEMENT ───────────── */}
      <section className="bg-sand py-16 lg:py-20">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <h2 className="text-center font-display text-2xl font-bold text-navy sm:text-3xl">
            Nous vous accompagnons à chaque étape
          </h2>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {ACCOMPAGNEMENT.map((e, i) => {
              const Icon = ETAPE_ICONS[i]
              return (
                <div key={e.titre} className="rounded-2xl border border-navy/10 bg-white p-6 shadow-card">
                  <span className="grid h-12 w-12 place-items-center rounded-xl bg-navy text-white">
                    <Icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-4 font-display text-lg font-semibold text-navy">{e.titre}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink/70">{e.texte}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ───────────── HISTOIRE FAMILIALE ───────────── */}
      <section id="histoire" className="bg-sand py-20 lg:py-24">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <img
                src="/img/equipe-familiale.png"
                alt="La famille Gilles — Justin, Joseph et Gustave"
                loading="lazy"
                decoding="async"
                width={900}
                height={909}
                className="mx-auto w-full max-w-md"
              />
            </div>
            <div className="lg:col-span-7">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-orange">
                Notre histoire
              </p>
              <h2 className="mt-3 font-display text-3xl font-bold text-navy sm:text-4xl">
                Maison Gilles, une histoire de famille
              </h2>

              <div className="mt-7 space-y-6 text-[0.97rem] leading-relaxed text-ink/75">
                <div>
                  <h3 className="font-display text-lg font-semibold text-navy">Un héritage enraciné</h3>
                  <p className="mt-1.5">
                    Depuis 1925, Maison Gilles prend racine à Jambes (Namur), au cœur d'une région que
                    nous connaissons par cœur. Transmise de père en fils, notre entreprise familiale grandit
                    depuis quatre générations autour de la même passion. À chaque époque, les valeurs sont
                    restées les mêmes : le respect du métier, la rigueur dans le travail, et le lien de
                    proximité avec notre clientèle. Un héritage qui ne se résume pas à des dates, mais à une
                    manière d'être, de conseiller, et de transmettre le flambeau avec fierté.
                  </p>
                </div>
                <div>
                  <h3 className="font-display text-lg font-semibold text-navy">Un savoir-faire</h3>
                  <p className="mt-1.5">
                    Chaque génération a transmis son expérience, aujourd'hui partagée par toute notre
                    équipe. Notre expertise s'est affinée avec le temps, au service de conseils justes et
                    de solutions adaptées. Ce savoir-faire, nourri par la pratique, fait aujourd'hui toute
                    notre différence.
                  </p>
                </div>
                <div>
                  <h3 className="font-display text-lg font-semibold text-navy">Une équipe passionnée</h3>
                  <p className="mt-1.5">
                    Notre histoire ne s'écrit pas qu'en famille : elle se construit aussi avec nos
                    collaborateurs. Techniciens et conseillers partagent les mêmes valeurs et le même goût
                    du travail bien fait. Au quotidien, ils contribuent eux aussi à faire vivre la Maison
                    Gilles.
                  </p>
                </div>
                <div>
                  <h3 className="font-display text-lg font-semibold text-navy">Une confiance durable</h3>
                  <p className="mt-1.5">
                    Nous prenons le temps d'écouter chaque client, de comprendre ses besoins et d'apporter
                    des conseils adaptés. Cette attention, nous la cultivons depuis toujours. C'est elle qui
                    nous permet de tisser, au fil des années, des relations solides et sincères.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────── CONTACT / LOCALISATION ───────────── */}
      <section id="contact" className="bg-white py-20 lg:py-24">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-orange">Contact</p>
            <h2 className="mt-3 font-display text-3xl font-bold text-navy sm:text-4xl">Où nous trouver ?</h2>
            <p className="mt-3 text-ink/70">{SITE.adresse} — {SITE.cp} {SITE.ville}</p>
          </div>

          <div className="mt-10 grid gap-6 lg:grid-cols-2">
            <div className="overflow-hidden rounded-2xl border border-navy/10 shadow-card">
              <img
                src="/img/magasin-situation.jpg"
                alt="La devanture du magasin Maison Gilles à Jambes"
                loading="lazy"
                decoding="async"
                width={1400}
                height={933}
                className="h-[24rem] w-full object-cover sm:h-[28rem]"
              />
            </div>
            <div className="overflow-hidden rounded-2xl border border-navy/10 shadow-card">
              {showMap ? (
                <iframe
                  title="Plan d'accès Maison Gilles"
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(SITE.mapsQuery)}&z=15&output=embed`}
                  className="h-[24rem] w-full sm:h-[28rem]"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setShowMap(true)}
                  className="group flex h-[24rem] w-full flex-col items-center justify-center gap-3 bg-sand p-6 text-center transition-colors hover:bg-sand/70 sm:h-[28rem]"
                  aria-label="Afficher la carte Google Maps"
                >
                  <span className="grid h-14 w-14 place-items-center rounded-full bg-navy text-white">
                    <MapPin className="h-7 w-7" />
                  </span>
                  <span className="font-display text-lg font-semibold text-navy">Afficher le plan d'accès</span>
                  <span className="inline-flex items-center gap-2 rounded-full bg-orange px-5 py-2.5 text-sm font-semibold text-white transition-colors group-hover:bg-orange-dk">
                    Charger la carte Google
                  </span>
                  <span className="text-xs text-ink/45">Fournie par Google Maps</span>
                </button>
              )}
            </div>
          </div>

          <div className="mt-6 flex justify-center">
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(SITE.mapsQuery)}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-navy-dk"
            >
              <MapPin className="h-4 w-4" /> Voir l'itinéraire
            </a>
          </div>
        </div>
      </section>

      {/* Confirmation de redirection vers un site externe */}
      {redirect && (
        <div className="fixed inset-0 z-[70] grid place-items-center bg-ink/50 p-4" onClick={() => setRedirect(null)}>
          <div role="dialog" aria-modal="true" aria-label="Redirection vers un site externe" className="w-full max-w-md rounded-2xl bg-white p-6 text-center shadow-soft" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-end">
              <button onClick={() => setRedirect(null)} className="rounded-lg p-1.5 text-ink/50 hover:bg-sand" aria-label="Fermer">
                <X className="h-5 w-5" />
              </button>
            </div>
            <span className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-orange/10 text-orange">
              <ExternalLink className="h-6 w-6" />
            </span>
            <h3 className="mt-4 font-display text-xl font-bold text-navy">Ouverture dans un nouvel onglet</h3>
            <p className="mt-2 text-sm leading-relaxed text-ink/70">
              Le site officiel de <strong className="text-navy">{redirect.marque}</strong> va s'ouvrir dans un nouvel onglet. Vous ne quittez pas Maison Gilles.
            </p>
            <div className="mt-6 flex justify-center gap-3">
              <button
                onClick={() => setRedirect(null)}
                className="rounded-full border border-navy/20 px-5 py-2.5 text-sm font-semibold text-navy transition-colors hover:bg-sand"
              >
                Annuler
              </button>
              <button
                onClick={confirmer}
                className="rounded-full bg-orange px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-orange-dk"
              >
                OK, ouvrir
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
